/**
 * Tracks the newest authoritative Engine document revision per session.
 *
 * Session snapshots and sync responses record a known revision, while
 * document.changed notifications call accept() before mutating Editor state.
 * Keeping this outside the Zustand execution store makes the ordering contract
 * explicit and independently testable.
 */
export class SessionRevisionTracker {
    private readonly latestBySession = new Map<string, number>();

    record(sessionId: string, revision: number | undefined): void {
        if (revision === undefined) return;
        const current = this.latestBySession.get(sessionId);
        if (current === undefined || revision > current) {
            this.latestBySession.set(sessionId, revision);
        }
    }

    accept(sessionId: string, revision: number | undefined): boolean {
        if (revision === undefined) return true;
        const current = this.latestBySession.get(sessionId);
        if (current !== undefined && revision < current) return false;
        this.latestBySession.set(sessionId, revision);
        return true;
    }

    get(sessionId: string): number | undefined {
        return this.latestBySession.get(sessionId);
    }

    clear(): void {
        this.latestBySession.clear();
    }
}

export const sessionRevisionTracker = new SessionRevisionTracker();
